/**
 * Part and Image utilities for canvas operations
 * Extracted from MyCanvas.js to reduce file size
 */

import { v4 as uuidv4 } from "uuid";
import { generateMarkerId } from "./lineUtils";
import { compressImage } from "./imageCompressionUtils";
import { saveToIndexedDB } from "../../../util/indexedDBUtils";

/**
 * Creates a function to handle part selection
 * @param {Function} generateMarkerId - Function to generate marker IDs
 * @param {string|null} selectedTextId - Currently selected text ID
 * @param {Array} texts - Current texts array
 * @param {Function} setTexts - Setter for texts array
 * @param {Object|null} selectedText - Currently selected text object
 * @param {Function} setSelectedText - Setter for selected text
 * @param {Function} setPartsModalOpen - Setter for parts modal open state
 * @param {Function} saveToHistory - Function to save to history
 * @param {boolean} isElementBeingEdited - Whether element is currently being edited
 * @param {Function} setPendingChanges - Function to mark pending changes
 * @param {Object} pendingChangesRef - Ref for synchronous pending changes updates
 * @returns {Function} Function to handle part selection
 */
export function createHandleSelectPart({
  generateMarkerId,
  selectedTextId,
  texts,
  setTexts,
  selectedText,
  setSelectedText,
  setPartsModalOpen,
  saveToHistory,
  onPartAdded, // Optional callback to trigger UI updates after part is added
  isElementBeingEdited,
  setPendingChanges,
  pendingChangesRef,
  offCanvasTexts = [], // Add off-canvas texts parameter
  setOffCanvasTexts = null, // Add off-canvas texts setter parameter
}) {
  return async function handleSelectPart(selectedPart) {
    const partNumber = selectedPart?.part_number || "";
    const description = selectedPart?.description || "";

    let defaultUnit = "";
    let defaultUnitId = "";
    if (
      selectedPart?.units &&
      selectedPart.units.length > 0 &&
      selectedPart.units[0]?.unit_name
    ) {
      defaultUnit = selectedPart.units[0].unit_name;
      defaultUnitId = selectedPart.units[0].unit_id || "";
    }

    const newPart = {
      id: generateMarkerId(), // 🔐 Unique ID for tracking this specific instance
      part: `(${partNumber}) ${description}`,
      qty: "",
      unit: "",
      itemCodeNote: selectedPart.textbox_note || "", // System-generated note from Item Code Table (always shows below part)
      note: "", // User's note (starts blank, shows below itemCodeNote)
      task: "",
      part_ref: selectedPart.part_id,
      defaultUnit,
      units: defaultUnitId, // Add unit_id
      unit_type_id: selectedPart?.unit_type_id_ref || null, // Add unit type ID for proper unit filtering
    };

    const type = selectedPart?.type_details?.name || "Other";
    const order = parseInt(selectedPart?.type_details?.orders || 9999);

    // Determine which text array and setter to use
    const isInMainCanvas = texts && texts.some((t) => t.id === selectedTextId);
    const isInOffCanvas = offCanvasTexts && offCanvasTexts.some((t) => t.id === selectedTextId);
    
    const targetTexts = isInOffCanvas ? offCanvasTexts : texts;
    const targetSetTexts = isInOffCanvas ? setOffCanvasTexts : setTexts;

    const updatedTexts = targetTexts.map((text) => {
      if (text.id === selectedTextId) {
        const updatedPartsByType = { ...text.partsByType };

        const existingParts = updatedPartsByType?.[type]?.parts || [];
        const updatedParts = [...existingParts, newPart]
          .map((part) => ({
            ...part,
            id: part.id || uuidv4(), // Ensure every part has a unique id
          }))
          .sort((a, b) =>
            a.part.toLowerCase().localeCompare(b.part.toLowerCase())
          );

        updatedPartsByType[type] = {
          order: updatedPartsByType?.[type]?.order || order,
          parts: updatedParts,
        };

        return {
          ...text,
          partsByType: updatedPartsByType,
          selectedContent: {
            id: newPart.id,
            type: "part",
            content: `${newPart.part} - Qty-${newPart.qty} ${newPart.unit}`,
            qty: newPart.qty,
            unit: newPart.unit,
            typeKey: type,
            part_ref: newPart.part_ref,
            unit_type_id: newPart.unit_type_id, // Include unit type ID in selected content
          },
        };
      }
      return text;
    });

    targetSetTexts(updatedTexts);

    // Update selectedText state first
    const updatedSelectedText = {
      ...selectedText,
      partsByType: {
        ...selectedText.partsByType,
        [type]: {
          order: selectedText.partsByType?.[type]?.order || order,
          parts: [
            ...(selectedText.partsByType?.[type]?.parts || []),
            newPart,
          ].sort((a, b) =>
            a.part.toLowerCase().localeCompare(b.part.toLowerCase())
          ),
        },
      },
      selectedContent: {
        id: newPart.id,
        type: "part",
        content: `${newPart.part} - Qty-${newPart.qty} ${newPart.unit}`,
        qty: newPart.qty,
        unit: newPart.unit,
        typeKey: type,
        part_ref: newPart.part_ref,
        unit_type_id: newPart.unit_type_id,
      },
    };

    setSelectedText(updatedSelectedText);

    // Store complete part details in IndexedDB for later use in past_canvas_data_json
    try {
      await saveToIndexedDB("partDetails", selectedPart.part_id, selectedPart);
    } catch (error) {
      console.error("❌ Failed to store part details in IndexedDB:", error);
    }

    // CRITICAL: If element is being edited, just mark pending changes instead of saving to history
    // This ensures part addition is grouped with other changes and saved on unselection
    if (isElementBeingEdited) {
      if (typeof setPendingChanges === "function") {
        setPendingChanges(true);
        if (pendingChangesRef) {
          pendingChangesRef.current = true;
        }
      }
    } else {
      // Element is not being edited (shouldn't happen normally), save to history immediately
      saveToHistory("add_part", null, null, null, null, updatedTexts);
    }

    setPartsModalOpen(false);

    // Call callback to trigger UI updates (e.g., show qty/unit section)
    if (onPartAdded) {
      // Use setTimeout to ensure state updates are complete before triggering UI changes
      setTimeout(() => {
        onPartAdded(newPart.id);
      }, 0);
    }
  };
}

/**
 * Creates a function to toggle image compression
 * @param {Array} images - Current images array
 * @param {Function} setImages - Setter for images array
 * @param {Object|null} selectedObject - Currently selected object
 * @param {Function} setSelectedObject - Setter for selected object
 * @param {Object|null} selectedImage - Currently selected image
 * @param {Function} setSelectedImage - Setter for selected image
 * @param {Function} saveToHistory - Function to save to history
 * @returns {Function} Function to toggle image compression
 */
export function createToggleImageCompression({
  images,
  setImages,
  offCanvasImages = [],
  setOffCanvasImages = null,
  selectedObject,
  setSelectedObject,
  selectedImage,
  setSelectedImage,
  saveToHistory,
}) {
  return async function toggleImageCompression(imageId, enableCompression) {

    // 🎯 MEMORY OPTIMIZATION: When compress=true, we display the compressed version
    // which is typically 200KB max (vs potentially several MB for originals)
    // This dramatically reduces memory footprint for rendering and DOM operations

    // Check if image is in main canvas
    const isInMainCanvas = images?.some(img => img.id === imageId) || false;
    const isInOffCanvas = offCanvasImages?.some(img => img.id === imageId) || false;


    // Update main canvas images if image is there
    if (isInMainCanvas) {
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (img.id !== imageId) return img;


          // Enable Compression
          if (enableCompression) {
            // If we don't have a compressed version yet, create it
            if (!img.compressedImage) {
              // For cropped images, compress the uncompressed cropped version if available
              // For non-cropped images, compress the original image
              let imageToCompress;
              if (img.cropData) {
                // For cropped images, compress the uncompressed cropped version
                imageToCompress = img.uncompressedCroppedImage || img.image;
              } else {
                // For non-cropped images, compress the original
                imageToCompress = img.originalImage || img.image;
              }

              const compressedImage = await compressImage(imageToCompress, 0.5);
              return {
                ...img,
                compress: true,
                originalImage: img.originalImage || img.image, // Kept for uncropping/quality restoration
                compressedImage,
                image: compressedImage, // ✅ Show compressed version (saves memory!)
              };
            } else {
              // We already have a compressed version, just use it
              return {
                ...img,
                compress: true,
                image: img.compressedImage, // ✅ Show compressed version (saves memory!)
              };
            }
          }
          // Disable Compression - restore original
          else {
            // For cropped images: We want to show the uncompressed cropped version if it exists
            // For non-cropped images: We want to show the original uncompressed image
            let imageToRestore;
            if (img.cropData) {
              // For cropped images, use the uncompressed cropped version if available
              // Otherwise fall back to the original uncropped image
              imageToRestore =
                img.uncompressedCroppedImage || img.originalImage || img.image;
            } else {
              // For non-cropped images, restore the original uncompressed image
              imageToRestore = img.originalImage || img.image;
            }

            return {
              ...img,
              compress: false,
              image: imageToRestore, // Show original/uncompressed version
            };
          }
        })
      );


      setImages(updatedImages);
    }

    // Update off-canvas images if image is there
    if (isInOffCanvas && setOffCanvasImages) {
      const updatedOffCanvasImages = await Promise.all(
        offCanvasImages.map(async (img) => {
          if (img.id !== imageId) return img;


          // Enable Compression
          if (enableCompression) {
            // If we don't have a compressed version yet, create it
            if (!img.compressedImage) {
              // For cropped images, compress the uncompressed cropped version if available
              // For non-cropped images, compress the original image
              let imageToCompress;
              if (img.cropData) {
                // For cropped images, compress the uncompressed cropped version
                imageToCompress = img.uncompressedCroppedImage || img.image;
              } else {
                // For non-cropped images, compress the original
                imageToCompress = img.originalImage || img.image;
              }

              const compressedImage = await compressImage(imageToCompress, 0.5);
              return {
                ...img,
                compress: true,
                originalImage: img.originalImage || img.image, // Kept for uncropping/quality restoration
                compressedImage,
                image: compressedImage, // ✅ Show compressed version (saves memory!)
              };
            } else {
              // We already have a compressed version, just use it
              return {
                ...img,
                compress: true,
                image: img.compressedImage, // ✅ Show compressed version (saves memory!)
              };
            }
          }
          // Disable Compression - restore original
          else {
            // For cropped images: We want to show the uncompressed cropped version if it exists
            // For non-cropped images: We want to show the original uncompressed image
            let imageToRestore;
            if (img.cropData) {
              // For cropped images, use the uncompressed cropped version if available
              // Otherwise fall back to the original uncropped image
              imageToRestore =
                img.uncompressedCroppedImage || img.originalImage || img.image;
            } else {
              // For non-cropped images, restore the original uncompressed image
              imageToRestore = img.originalImage || img.image;
            }

            return {
              ...img,
              compress: false,
              image: imageToRestore, // Show original/uncompressed version
            };
          }
        })
      );


      setOffCanvasImages(updatedOffCanvasImages);
    }

    // Also update selectedObject if it's the same
    if (selectedObject?.id === imageId) {
      // Find the updated image from the appropriate array
      let updated = null;
      if (isInMainCanvas) {
        // Get the updated image from main canvas (we need to wait for state update)
        setTimeout(() => {
          setImages(currentImages => {
            updated = currentImages.find((img) => img.id === imageId);
            if (updated) {
              setSelectedObject({ ...updated, type: "Image" });
            }
            return currentImages; // Don't modify the array, just use it to get the updated image
          });
        }, 0);
      } else if (isInOffCanvas) {
        // Get the updated image from off-canvas (we need to wait for state update)
        setTimeout(() => {
          if (setOffCanvasImages) {
            setOffCanvasImages(currentOffCanvasImages => {
              updated = currentOffCanvasImages.find((img) => img.id === imageId);
              if (updated) {
                setSelectedObject({ ...updated, type: "Image" });
              }
              return currentOffCanvasImages; // Don't modify the array, just use it to get the updated image
            });
          }
        }, 0);
      }
    }

    // Also update selectedImage if it's the same
    if (selectedImage?.id === imageId) {
      // Find the updated image from the appropriate array
      setTimeout(() => {
        if (isInMainCanvas) {
          setImages(currentImages => {
            const updated = currentImages.find((img) => img.id === imageId);
            if (updated) {
              setSelectedImage(updated);
            }
            return currentImages; // Don't modify the array, just use it to get the updated image
          });
        } else if (isInOffCanvas && setOffCanvasImages) {
          setOffCanvasImages(currentOffCanvasImages => {
            const updated = currentOffCanvasImages.find((img) => img.id === imageId);
            if (updated) {
              setSelectedImage(updated);
            }
            return currentOffCanvasImages; // Don't modify the array, just use it to get the updated image
          });
        }
      }, 0);
    }

    saveToHistory();
  };
}
